import { AxiosError } from "axios";
import { getRemappedErrors } from "PFApp/booking/components/booking_form/get_remapped_errors";
import { Overbooking } from "PFApp/booking/types";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { ActivityWarning } from "PFCore/hooks/queries/activity";
import { FeatureFlag, Profile } from "PFTypes";
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

import { BookingDataItem, BookingFormMode } from "../booking_form.types";
import { clearKnownErrors, setKnownErrors } from "../booking_form.utils";
import { BookingFormValues } from "../booking_form_values.types";
import { useAreBookingPhasesValid } from "../use_are_booking_phases_valid";
import { isGroupOption } from "../workforce_member_select/workforce_member_select.utils";
import { FetchPotentialWarnings } from "./fetch_potential_warnings";
import { getAvailabilityAffectedBookings } from "./get_availability_affected_bookings";
import { usePotentialWarningsQuery } from "./use_potential_warnings_query";

const POTENTIAL_WARNINGS_LOADING = "potential-warnings-loading";

type UsePotentialWarnings = {
  mode: BookingFormMode;
  setLoadingState: Dispatch<SetStateAction<Record<string, boolean>>>;
  initialBookingsRef: RefObject<BookingDataItem[] | null>;
};
export type PotentialWarnings = {
  overbookings: Overbooking[];
  misalignments: ActivityWarning[];
  exeedsCalendarHours: boolean;
  includesNonWorkingDays: boolean;
  overbookingIntersectionsDates: string[];
};

export const usePotentialWarnings = ({
  mode,
  setLoadingState,
  initialBookingsRef
}: UsePotentialWarnings): Partial<PotentialWarnings> => {
  const isBookingModuleEnabled = useIsFeatureEnabled()(FeatureFlag.BookingModule);
  const { getSingleErrorByType } = useResponseErrors();
  const hasInitialFetchCompleted = useRef(false);

  const { setError, clearErrors } = useFormContext<BookingFormValues>();

  const { isValid, isSubmitting } = useFormState();

  const [bookings, activityId, workforceMember] = useWatch<
    BookingFormValues,
    ["bookings", "activityId", "workforceMember", "bookingType"]
  >({
    name: ["bookings", "activityId", "workforceMember", "bookingType"]
  });

  const areBookingsValid = useAreBookingPhasesValid(mode);
  const isFormValid = isValid && areBookingsValid;

  const isEditAllMode = mode === BookingFormMode.EditAll;
  const initialBookings = initialBookingsRef.current ?? [];

  const hasAffectedBookings = useMemo(() => {
    if (!hasInitialFetchCompleted.current && initialBookings.length > 0) {
      return true;
    }
    if (!isEditAllMode || initialBookings.length === 0 || !bookings || bookings.length === 0) {
      return true;
    }
    const affectedBookings = getAvailabilityAffectedBookings(
      bookings as BookingDataItem[],
      initialBookings,
      mode
    );
    return affectedBookings.length > 0;
  }, [bookings, initialBookings, mode, isEditAllMode]);

  const shouldBeDisabled =
    !isFormValid ||
    isSubmitting ||
    !workforceMember ||
    isGroupOption(workforceMember) ||
    !hasAffectedBookings;

  const queryEnabled = isBookingModuleEnabled && !shouldBeDisabled;

  const formValues = useWatch<BookingFormValues>();

  const { data, isLoading, isSuccess, isError, error, dataUpdatedAt, errorUpdatedAt } =
    usePotentialWarningsQuery(
      {
        values: formValues as FetchPotentialWarnings["values"],
        mode,
        initialBookings
      },
      {
        enabled: queryEnabled,
        retry: 0,
        gcTime: 0,
        select: (response) =>
          ({
            ...response,
            misalignments: activityId
              ? (response.misalignments ?? []).filter(({ profileIds }) =>
                  profileIds.includes((workforceMember as Profile).id)
                )
              : []
          }) as PotentialWarnings
      }
    );

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    hasInitialFetchCompleted.current = true;
    clearKnownErrors(mode, bookings, clearErrors, "potential-warnings");
  }, [dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isError || !error) {
      return;
    }
    if (error.statusText === "abort" || error.code === "ERR_CANCELED") {
      return;
    }

    hasInitialFetchCompleted.current = true;

    const responseErrors = getSingleErrorByType(error);
    if (Object.keys(responseErrors).length > 0) {
      const errors = getRemappedErrors(error as unknown as AxiosError, mode, responseErrors, bookings);
      setKnownErrors(mode, bookings, errors, setError, clearErrors, "potential-warnings");
    }
  }, [errorUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (queryEnabled && isLoading) {
      setLoadingState((prev) => ({ ...prev, [POTENTIAL_WARNINGS_LOADING]: true }));
    } else {
      setLoadingState((prev) => ({ ...prev, [POTENTIAL_WARNINGS_LOADING]: false }));
    }
  }, [isLoading, queryEnabled]);

  return (data ?? {}) as Partial<PotentialWarnings>;
};
